import * as indexedDBService from '../utils/indexedDBService';

export interface InvoiceSettings {
    numberPrefix: string;
    defaultTemplate: number;
    useCustomPrefix: boolean;
    paperSize: string;
    pdfQuality: string;
    includeDueDate: boolean;
    includePaymentInfo: boolean;
    showWatermark: boolean;
    includeLogo: boolean;
    defaultTerms: string;
    defaultNotes: string;
    defaultDueDays: number;
    defaultTaxRate: number;
    emailSubject: string;
    emailBody: string;
    ccToSelf: boolean;
    attachPdf: boolean;
    enableSaveAndSend?: boolean;
    printCollectingCopy?: boolean;
}

export interface SmtpSettings {
    host: string;
    port: string;
    username: string;
    password?: string;
    secure: boolean;
}

export interface BusinessProfileSettings {
    logoUrl?: string;
    primaryColor?: string;
    secondaryColor?: string;
    upiId?: string;
    bankName?: string;
    accountNumber?: string;
    ifscCode?: string;
}

export interface UserSettings {
    invoiceSettings?: InvoiceSettings;
    payrollEnabled?: boolean;
    taxRegime?: string;
    gstFilingFrequency?: string;
    inventoryTrackingEnabled?: boolean;
    autoBackupEnabled?: boolean;
    payStructure?: any[];
    customTemplates?: any[];
    smtpSettings?: SmtpSettings;
    businessProfile?: BusinessProfileSettings;
    updatedAt?: string;
    createdAt?: string;
}

export class SettingsService {
    private static COLLECTION = 'userSettings';

    /**
     * Fetch user settings
     * @param userId User ID
     * @returns Promise with user settings or null
     */
    public static async fetchSettings(userId: string): Promise<UserSettings | null> {
        try {
            const settings = await indexedDBService.getItemById<UserSettings>(this.COLLECTION, userId);
            return settings || null;
        } catch (error) {
            console.error('Error fetching settings:', error);
            return null;
        }
    }

    /**
     * Save user settings
     * @param userId User ID
     * @param settings Settings to save
     */
    public static async saveSettings(userId: string, settings: UserSettings): Promise<void> {
        try {
            await indexedDBService.putItem(this.COLLECTION, {
                ...settings,
                id: userId, // Use userId as the document ID for userSettings
                updatedAt: new Date().toISOString()
            });
        } catch (error) {
            console.error('Error saving settings:', error);
            throw error;
        }
    }

    /**
     * Update specific settings
     * @param userId User ID
     * @param updates Partial settings to update
     */
    public static async updateSettings(userId: string, updates: Partial<UserSettings>): Promise<void> {
        try {
            const currentSettings = await this.fetchSettings(userId) || {};
            const updatedSettings = {
                ...currentSettings,
                ...updates,
                updatedAt: new Date().toISOString()
            };

            await this.saveSettings(userId, updatedSettings);
        } catch (error) {
            console.error('Error updating settings:', error);
            throw error;
        }
    }
}

export default SettingsService;
